import { FC, useEffect, useState } from "react";
import Layout from "@/components/layout";
import { useTranslation } from "react-i18next";
import styles from "./index.module.scss";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import useCashRegisterStore, { useIsTakeOut } from "@/stores/cash-register";
import api from "@/services/api";
import { updateShopTheme } from "@/helpers/css-variables";
import ErrorScreen from "@/components/error-screen";
import LoadingScreen from "@/components/loading-screen";
import ScannerHandler from "@/helpers/ScannerHandler";
import { Routes } from "@/types/routes";
import useTransactionSocketStore from "@/stores/transaction-socket";
import useReloadBundle from "@/hooks/useReloadBundle";
import { playSound, SoundOption } from "@/helpers/playSound";
import usePickupStore from "@/stores/pickup";
import packageInfo from "../../../package.json";
import { Button } from "@/components/button";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { setInitialEan } from "@/store/ui/slice";
import { newCustomerCleanup } from "@/store/actions";
import { fetchProducts } from "@/store/product/async-thunk";

const StartPage: FC = () => {
  useReloadBundle();
  const dispatch = useAppDispatch();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const pickupStore = usePickupStore();
  const { setCurrentCashRegister, current: currentCashRegister } = useCashRegisterStore();
  const [error, setError] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const { socketHandler } = useTransactionSocketStore();
  const isTakeOut = useIsTakeOut();

  const onContinue = async (eanToAdd?: string) => {
    playSound(SoundOption.Start);
    dispatch(newCustomerCleanup());
    pickupStore.reset();

    if (eanToAdd) dispatch(setInitialEan(eanToAdd));
    await queryClient.resetQueries();
    navigate(Routes.MAIN);
    dispatch(fetchProducts());
  };

  const fetchAndUpdateCashRegister = async () => {
    try {
      setError(false);
      setLoading(true);
      const { data } = await api.cashRegisters.getCurrent();
      setCurrentCashRegister(data.data);
      updateShopTheme(data.data);
      i18n.changeLanguage(data.data.shop.language);
    } catch (_e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (socketHandler.isConnected()) socketHandler.closeConnection();
    i18n.changeLanguage(currentCashRegister?.shop.language ?? "nl");

    fetchAndUpdateCashRegister();

    const onScanEan = (ean: string) => {
      onContinue(ean);
    };

    const scannerHandler = new ScannerHandler(onScanEan);
    scannerHandler.start();
    return () => scannerHandler.stop();
  }, []);

  if (loading) return <LoadingScreen />;
  if (error || !currentCashRegister) return <ErrorScreen onTryAgain={fetchAndUpdateCashRegister} />;

  const backgroundImage = currentCashRegister.shop.property.theme.startscreenImageCdnUrl;

  return (
    <Layout childrenClassName={styles.container} onClick={() => onContinue()} hasAnimation>
      <h1 className={isTakeOut ? styles.title : styles.pickupTitle} data-cy="start-page-title">
        {isTakeOut ? t("startPage.title") : t("startPage.pickupTitle")}
      </h1>
      <div className={styles.startBox}>
        <h2 className={styles.subtitle}>{t("startPage.scanBarCode")}</h2>
        <Button text={t("ui.start")} />
        <p className={styles.version}>{t("startPage.version", { version: packageInfo.version })}</p>
      </div>
      <div className={styles.imageWrapper}>
        <div className={styles.wave} />
        <img className={styles.image} src={backgroundImage} alt="Breakfast" />
      </div>
    </Layout>
  );
};

export default StartPage;
